import { useCallback, useEffect, useRef, useState } from 'react';

interface UseDropdownOptions {
    /** Extra cleanup run whenever the dropdown closes (e.g. reset a hover state). */
    onClose?: () => void;
    /** Close when Escape is pressed while open. Default true. */
    closeOnEscape?: boolean;
    /** Close on a mousedown outside the wrapper ref while open. Default true. */
    closeOnOutsideClick?: boolean;
}

/**
 * Shared open/close lifecycle for the toolbar menus: owns the open flag,
 * exposes a wrapper `ref`, and — only while open — closes on Escape and on a
 * mousedown outside that ref. Replaces the per-menu copy of this effect that
 * was duplicated across 10 toolbar components with one consistent source.
 *
 * `onClose` is read through a ref so passing an inline callback does not
 * re-subscribe the document listeners on every render — they attach once per
 * open/close transition, exactly like the hand-rolled version did.
 */
export function useDropdown<T extends HTMLElement = HTMLDivElement>(options: UseDropdownOptions = {}) {
    const { closeOnEscape = true, closeOnOutsideClick = true } = options;
    const [open, setOpen] = useState(false);
    const ref = useRef<T>(null);

    const onCloseRef = useRef(options.onClose);
    onCloseRef.current = options.onClose;

    const close = useCallback(() => {
        setOpen(false);
        onCloseRef.current?.();
    }, []);

    const toggle = useCallback(() => setOpen((o) => !o), []);

    useEffect(() => {
        if (!open) return;
        const handlePointerDown = (e: MouseEvent) => {
            if (closeOnOutsideClick && ref.current && !ref.current.contains(e.target as Node)) close();
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (closeOnEscape && e.key === 'Escape') close();
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, close, closeOnEscape, closeOnOutsideClick]);

    return { open, setOpen, toggle, close, ref };
}
